import { execSync } from "node:child_process";
import { existsSync } from "node:fs";
import { join } from "node:path";
import { cwd } from "node:process";
import { cancel, intro, outro, spinner } from "@clack/prompts";
import kleur from "kleur";

const { green, yellow, cyan, bold, red, gray } = kleur;

function runGitCommand(cmd: string): string {
  try {
    return execSync(cmd, {
      cwd: cwd(),
      encoding: "utf-8",
      stdio: ["pipe", "pipe", "pipe"],
    });
  } catch (_error) {
    return "";
  }
}

function isValidGitRepo(): boolean {
  try {
    execSync("git rev-parse --git-dir", {
      cwd: cwd(),
      stdio: "pipe",
    });
    return true;
  } catch {
    return false;
  }
}

function getContextPath(): string {
  return join(cwd(), ".bumper", "context.md");
}

function hasContextFile(): boolean {
  return existsSync(getContextPath());
}

function getGitDiff(since: string | null): string {
  // const contextPath = getContextPath();
  const relativePath = ".bumper/context.md";

  if (!since) {
    // Default: show last 5 commits
    return runGitCommand(
      `git log --oneline -5 --follow -- "${relativePath}" 2>/dev/null || echo ""`,
    );
  }

  if (since === "main" || since === "master") {
    // Compare with main/master branch
    return runGitCommand(
      `git diff ${since}...HEAD -- "${relativePath}" 2>/dev/null || git diff ${since} HEAD -- "${relativePath}" 2>/dev/null || echo ""`,
    );
  }

  // Assume it's a commit range or commit SHA
  return runGitCommand(
    `git diff ${since} -- "${relativePath}" 2>/dev/null || echo ""`,
  );
}

function formatDiffOutput(diff: string): string {
  const lines = diff.split("\n");
  let output = "";
  let addCount = 0;
  let removeCount = 0;

  for (const line of lines) {
    if (line.startsWith("+") && !line.startsWith("+++")) {
      output += green(`+ ${line.substring(1)}\n`);
      addCount++;
    } else if (line.startsWith("-") && !line.startsWith("---")) {
      output += red(`- ${line.substring(1)}\n`);
      removeCount++;
    } else if (line.startsWith("@@")) {
      output += bold(`\n${cyan(line)}\n`);
    } else if (line.trim()) {
      output += gray(` ${line}\n`);
    }
  }

  const summary = `\n${bold("Summary:")} ${green(`+${addCount}`)} ${red(`-${removeCount}`)}\n`;
  return output + summary;
}

export async function diffCommand(args: string[]) {
  intro(cyan(bold("📊 Bumper Context Diff")));

  const s = spinner();

  try {
    s.start("Validating repository");

    if (!isValidGitRepo()) {
      cancel("❌ Not a git repository");
      process.exit(1);
    }

    if (!hasContextFile()) {
      cancel("❌ No .bumper/context.md found");
      process.exit(1);
    }

    s.start("Fetching diff");

    let since: string | null = null;

    // Parse --since flag
    const sinceIndex = args.indexOf("--since");
    if (sinceIndex !== -1 && sinceIndex + 1 < args.length) {
      const sinceVal = args[sinceIndex + 1];
      since = sinceVal || null;
    }

    // Get the diff
    const diff = getGitDiff(since);

    s.stop();

    if (!diff || diff.trim() === "") {
      outro(yellow("! No changes found in context.md"));
      return;
    }

    console.log("\n");
    console.log(formatDiffOutput(diff));

    outro(
      [
        green("✓ Done"),
        "",
        "Tip: Use --since to specify a reference point:",
        `  ${yellow("bmpr diff --since main")}`,
        `  ${yellow("bmpr diff --since HEAD~5")}`,
        `  ${yellow("bmpr diff --since abc123")}`,
      ].join("\n"),
    );
  } catch (error) {
    s.stop();
    cancel("Failed to generate diff");
    if (error instanceof Error) {
      console.error("Error:", error.message);
    }
    process.exit(1);
  }
}
